import { useState, useEffect, useCallback } from "react";
import WriteDivField from "../Components/WriteDivField";
import { apisHeaders, normalizeNumber } from "../utils";
import { Button } from "primereact/button";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getInvoiceById, updateInvoice } from "../apis/invoiceSlice";
import { useNavigate, useParams } from "react-router-dom";
import { DeleteSweetAlert } from "src/DeleteSweetAlert";
import {
  getMatchingCreditEntitys,
  setCreditEntitys,
} from "src/apis/entitySlice";
import _ from "lodash";
import "../style.scss";
import axios from "axios";
import { Checkbox } from "primereact/checkbox";

const getInitialRow = () => ({
  item: "",
  pcs: "",
  qty: "",
  rate: "",
  amount: "",
});

export default function EditInvoice() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // State for invoice fields
  const [billNo, setBillNo] = useState("");
  const [entityName, setEntityName] = useState("");
  const [entityId, setEntityId] = useState("");
  const [writing, setWriting] = useState(false);
  const [invoice_type, setInvoice_type] = useState("");
  const [rows, setRows] = useState([]);
  const [btnLoading, setBtnLoading] = useState(false);
  const [itemLoadingRow, setItemLoadingRow] = useState(null);

  const { loggedIn } = useSelector((state) => state.authData);
  const { creditEntitys, entLoading, entityAns } = useSelector(
    (state) => state.entData
  );
  // Fetch invoice by id on mount
  useEffect(() => {
    async function fetchInvoice() {
      const response = await dispatch(getInvoiceById({ id }));
      const data = response?.payload?.data;
      if (data) {
        const newItems = data.items.map((x, index) => ({
          ...x,
          id: index,
        }));
        setEntityId(data.entityId || "");
        setEntityName(data.entityName || "");
        setBillNo(data.billNo || "");
        setInvoice_type(data?.invoice_type || "");
        setRows(newItems || []);
      }
    }
    if (id) fetchInvoice();
  }, [id, dispatch]);

  const handleCustInput = (value) => {
    sellDebouncedSearch(value);
  };

  const sellDebouncedSearch = useCallback(
    _.debounce(async (searchName) => {
      if (searchName) {
        const response = await dispatch(
          getMatchingCreditEntitys({
            uId: loggedIn?._id,
            type: "C",
            entName: searchName,
          })
        );
        // console.log("Credit response", response);
        if (response.payload?.error) {
          toast.error(response.payload.message);
        }
      }
    }, 2000),
    [loggedIn]
  );

  // Delete row by index and clear WriteDivField DOM content
  const deleteRow = (rowIndex) => {
    const newRows = rows.filter((_, i) => i !== rowIndex);
    setRows(newRows);
  };

  const handeRemoveRowConfirm = async (data, index) => {
    if (data && index !== undefined && index !== null) {
      const response = () => deleteRow(index);
      DeleteSweetAlert({
        title: "Are you sure you want to remove this entry?",
        icon1: "warning",
        title2: `${data?.item}`,
        callApi: response,
        callSuccessModal: false,
      });
    }
  };

  // Debounced API call for item search
  const debouncedItemSearch = useCallback(
    _.debounce(async (value, rowIndex, onDone) => {
      if (!value) {
        if (onDone) onDone();
        return;
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_API}/item/findItem`,
          { uId: loggedIn?._id, item_name: value },
          apisHeaders
        );
        const data = response?.data?.data;
        if (data && data?.item_name && data?.item_rate) {
          const updatedRows = [...rows];
          updatedRows[rowIndex] = {
            ...updatedRows[rowIndex],
            item: data.item_name,
            rate: data.item_rate.toString(),
          };
          // Optionally, recalculate amount if qty is present
          const qty = parseInt(updatedRows[rowIndex].qty || "0", 10);
          if (!isNaN(qty)) {
            updatedRows[rowIndex].amount = (
              qty * parseInt(data.item_rate, 10)
            ).toString();
          }
          if (
            rowIndex === updatedRows.length - 2 ||
            rowIndex === updatedRows.length - 1
          ) {
            for (let i = 0; i < 5; i++) {
              updatedRows.push(getInitialRow());
            }
          }
          setRows(updatedRows);
        }
        // If not found, do nothing
      } catch (err) {
        // Optionally handle error
        console.log("err", err);
      }
      if (onDone) onDone();
    }, 1000),
    [loggedIn, rows] // <-- add rows as dependency
  );

  // Example handleInput for rows:
  const handleInput = (rowIndex, field, value) => {
    const newRows = [...rows];
    if (field === "qty" || field === "amount") {
      const normalized = normalizeNumber(value);
      newRows[rowIndex][field] = normalized.replace(/[^0-9]/g, "");
    } else if (field === "item") {
      newRows[rowIndex][field] = value;
      setItemLoadingRow(rowIndex); // Set loading row
      debouncedItemSearch(value, rowIndex, () => setItemLoadingRow(null)); // Pass callback to clear loading
    } else {
      newRows[rowIndex][field] = value;
    }
    if (field === "qty" || field === "rate") {
      const qty = parseInt(
        field === "qty"
          ? normalizeNumber(value).replace(/[^0-9]/g, "")
          : newRows[rowIndex].qty || "0",
        10
      );
      const rate = parseInt(
        field === "rate"
          ? normalizeNumber(value).replace(/[^0-9]/g, "")
          : newRows[rowIndex].rate || "0",
        10
      );
      if (!isNaN(qty) && !isNaN(rate)) {
        newRows[rowIndex].amount = (qty * rate).toString();
      } else {
        newRows[rowIndex].amount = "";
      }
    }
    if (field !== "item") {
      if (rowIndex === newRows.length - 2 || rowIndex === newRows.length - 1) {
        for (let i = 0; i < 5; i++) {
          newRows.push({ ...getInitialRow(), id: rows.length + i });
        }
      }
    }
    setRows(newRows);
  };

  // Example handleSubmit:
  const handleSubmit = async () => {
    if (rows.filter((r) => r.item && r.item.trim() !== "").length === 0) {
      toast.warning("Please add at least one item.");
      return;
    }
    setBtnLoading(true);
    const finalData = {
      uId: loggedIn?._id,
      entityName,
      entityId,
      invoice_type,
      items: rows.filter((r) => r.item && r.item.trim() !== ""),
      qty: rows
        .map((r) => parseInt(r.qty || "0", 10))
        .reduce((a, b) => a + b, 0),
      totalAmt: rows
        .map((r) => parseInt(r.amount || "0", 10))
        .reduce((a, b) => a + b, 0),
      id: id,
    };
    const response = await dispatch(updateInvoice(finalData));
    if (!response?.payload?.error) {
      toast.success("Invoice updated successfully.");
      dispatch(setCreditEntitys([]));
      setTimeout(() => {
        setBtnLoading(false);
        navigate("/invoicelist");
      }, 1000);
      return;
    } else {
      toast.error(response?.payload?.message);
    }
    setBtnLoading(false);
  };

  return (
    <div className="m-[0.3125rem_0.375rem]">
      <div className="flex gap-2 items-center mt-2">
        <Button
          icon={"pi pi-home"}
          className="h-[3rem]"
          onClick={() => navigate("/")}
        />
        <Button
          icon={"pi pi-arrow-circle-left"}
          className="h-[3rem]"
          onClick={() => navigate("/invoicelist")}
        />
        <div className="w-[41%] relative">
          <WriteDivField
            onClick={() => setWriting(true)}
            onClear={() => {
              setEntityId("");
              setEntityName("");
            }}
            style={{ border: "1px solid black" }}
            parentClassName={`w-full h-[3rem] outline-none border border-black p-0.5`}
            className="w-full h-full outline-none text-[1.5rem] flex items-center"
            handleInput={(val) => {
              setEntityName(val);
              handleCustInput(val);
            }}
            placeholder="Customer"
            value={entityName || ""}
          />
          {writing && (
            <div className={"selectBox"}>
              {entLoading ? (
                <div
                  style={{ width: "100%", height: "12rem" }}
                  className="flex items-center justify-center"
                >
                  <i
                    className="pi pi-spin pi-spinner text-blue-500"
                    style={{ fontSize: "3rem" }}
                  ></i>
                </div>
              ) : (
                <>
                  <div
                    className="w-full py-1 px-2 bg-red-500 rounded-md flex items-center justify-center"
                    onClick={() => setWriting(false)}
                  >
                    <i className="pi pi-times text-white"></i>
                  </div>
                  {creditEntitys.length > 0 ? (
                    <>
                      {creditEntitys.map((item, index) => (
                        <option
                          key={index}
                          value={item._id}
                          className={`w-full py-1 px-2 rounded-md flex items-center ${
                            entityId === item._id
                              ? "bg-blue-500 text-white"
                              : "bg-gray-200"
                          }`}
                          onClick={() => {
                            setEntityId(item?._id);
                            setEntityName(item?.searchName);
                            setWriting(false);
                          }}
                        >
                          {item?.searchName}
                        </option>
                      ))}
                    </>
                  ) : (
                    <div
                      style={{
                        width: "100%",
                        height: "12rem",
                        fontWeight: "700",
                      }}
                      className="flex items-center justify-center"
                    >
                      No Data Found
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        {/* <WriteDivField
          style={{ border: "1px solid black" }}
          parentClassName={`w-[41%] h-[3rem] outline-none border border-black p-0.5`}
          className="w-full h-full outline-none text-[1.5rem] flex items-center"
          placeholder="Bill No"
          value={billNo}
          handleInput={() => {}}
          showClearIcon={false}
        /> */}
        <div
          className={`w-[41%]`}
        >
          <div className="w-full h-full outline-none text-[1.1rem] flex items-center">
            {billNo}
          </div>
          <div className="flex gap-6 items-center">
            <div className="flex items-center gap-2">
              <Checkbox
                inputId={`cash`}
                checked={invoice_type === "K"}
                onChange={() => setInvoice_type("K")}
              />
              <label htmlFor={`cash`}>Cash</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                inputId={`credit`}
                checked={invoice_type === "C"}
                onChange={() => setInvoice_type("C")}
              />
              <label htmlFor={`credit`}>Credit</label>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                inputId={`uchak`}
                checked={invoice_type === "U"}
                onChange={() => setInvoice_type("U")}
              />
              <label htmlFor={`uchak`}>Uchak</label>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-2 max-h-[92vh] overflow-y-scroll">
        {rows.map((row, index) => (
          <div
            key={index}
            className="flex select-none"
            tabIndex={0}
            onDoubleClick={() => handeRemoveRowConfirm(row, index)}
          >
            <div
              className={`w-[8%] h-[3rem] p-0.5`}
              style={{ border: "1px solid black" }}
            >
              <div className="w-full h-full outline-none text-[1.5rem] flex items-center ps-1">
                {index + 1}
              </div>
            </div>
            <WriteDivField
              style={{ border: "1px solid black" }}
              parentClassName={`w-[37%] h-[3rem] outline-none border border-black p-0.5`}
              className="w-full h-full outline-none text-[1.5rem] flex items-center"
              handleInput={(val) => handleInput(index, "item", val)}
              placeholder="Item"
              value={row.item}
            />
            <WriteDivField
              style={{ border: "1px solid black" }}
              parentClassName={`w-[15%] h-[3rem] outline-none border border-black p-0.5`}
              className="w-full h-full outline-none text-[1.5rem] flex items-center"
              handleInput={(val) => handleInput(index, "pcs", val)}
              placeholder="Pices"
              value={row.pcs}
            />
            <WriteDivField
              style={{ border: "1px solid black" }}
              parentClassName={`w-[15%] h-[3rem] outline-none border border-black p-0.5`}
              className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
              handleInput={(val) => handleInput(index, "qty", val)}
              placeholder="Qty"
              value={row.qty}
            />
            {itemLoadingRow === index ? (
              <div
                style={{ border: "1px solid black" }}
                className="w-[15%] h-[3rem] outline-none flex items-center justify-start p-0.5"
              >
                <i className="pi pi-spin pi-spinner text-blue-500 text-lg"></i>
              </div>
            ) : (
              <WriteDivField
                style={{ border: "1px solid black" }}
                parentClassName={`w-[15%] h-[3rem] outline-none border border-black p-0.5`}
                className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
                handleInput={(val) => handleInput(index, "rate", val)}
                placeholder="Rate"
                value={row.rate}
              />
            )}
            <WriteDivField
              style={{ border: "1px solid black" }}
              parentClassName={`w-[20%] h-[3rem] outline-none border border-black p-0.5`}
              className="w-11/12 h-full outline-none text-[1.5rem] flex items-center"
              handleInput={(val) => handleInput(index, "amount", val)}
              placeholder="Amount"
              value={row.amount}
            />
          </div>
        ))}
      </div>
      <div className="flex justify-between mt-2">
        <div className="text-xl font-bold">
          {`Total Item(S):${
            rows.filter((r) => r.item && r.item.trim() !== "").length
          }`}
          &nbsp;/ Qty:{" "}
          {rows
            .map((r) => parseInt(r.qty || "0", 10))
            .reduce((a, b) => a + b, 0)}
        </div>
        <div className="text-xl font-bold">
          {`Total: ${rows
            .map((r) => parseInt(r.amount || "0", 10))
            .reduce((a, b) => a + b, 0)}`}
        </div>
      </div>
      <div className="mt-2 flex justify-center items-center">
        <Button
          label="Edit"
          className="w-[90%] py-1.5"
          loading={btnLoading}
          onClick={handleSubmit}
        />
      </div>
    </div>
  );
}
